import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { useStore } from 'effector-react';
import { Empty, Pagination, Spin, Button, Input } from 'antd';
import { SortDescendingOutlined, SortAscendingOutlined } from '@ant-design/icons';
import user, { getPosts } from '@stores/user';
import { hidePost, block, unhidePost, facePost, removefacePost } from '@stores/posts';
import useTranslate from '@hooks/useTranslate';
import { goTo } from '@helpers/navigation';
import IUser from '@interfaces/IUser';
import IPost from '@interfaces/IPost';
import PostPreview from '../PostPreview/PostPreview';
import componentContent from './UserPosts.content.json';
import css from './UserPosts.module.less';

const { Search } = Input;

const pageSizeOptions = ['10', '30', '50'];

interface UserPostsProps {}

const preloaderTexts = {
  blockPost: (postID: IPost['id']) => `blockPost_${postID}`,
  hidePost: (postID: IPost['id']) => `hidePost_${postID}`,
  unhidePost: (postID: IPost['id']) => `unhidePost_${postID}`,
  facePost: (postID: IPost['id']) => `facePost_${postID}`,
  removefacePost: (postID: IPost['id']) => `removefacePost_${postID}`,
};

const UserPosts: FunctionComponent<UserPostsProps> = () => {
  const { posts, preloaders, pagination, item, filters: currentFilterParams } = useStore(user);
  const { userId } = useParams<{ userId: IUser['id'] }>();

  const content = useTranslate(componentContent);

  const isDisabledBlock = useMemo(() => {
    return !!preloaders.block;
  }, [preloaders]);

  const isDisabledHide = useMemo(() => {
    return !!(preloaders.hidePost || preloaders.unhidePost);
  }, [preloaders]);

  const isDisabledFace = useMemo(() => {
    return !!(preloaders.facePost || preloaders.removefacePost);
  }, [preloaders]);

  const handleBlockPost = useCallback(
    async (e: React.MouseEvent<HTMLElement, MouseEvent>, postID: IPost['id']) => {
      try {
        await block({
          postId: postID,
          preloaderId: preloaderTexts.blockPost(postID),
        });
        getPosts({ userId });
      } catch (e) {
        // Ignore error
      }
    },
    [],
  );

  const handleHidePost = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>, postID: IPost['id'], hide: boolean) => {
      const payload = {
        postId: postID,
        preloaderId: hide ? preloaderTexts.hidePost(postID) : preloaderTexts.unhidePost(postID),
      };
      if (hide) {
        hidePost(payload);
      } else {
        unhidePost(payload);
      }
    },
    [],
  );

  const handleFacePost = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>, postID: IPost['id'], face: boolean) => {
      const payload = {
        postId: postID,
        preloaderId: face ? preloaderTexts.facePost(postID) : preloaderTexts.removefacePost(postID),
      };
      if (face) {
        hidePost(payload).then(() => facePost(payload));
      } else {
        removefacePost(payload);
      }
    },
    [],
  );

  const handleOpenPost = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>, postID: IPost['id']) => {
      goTo('userDetailPosts', { userId, postId: postID });
    },
    [userId],
  );

  const paginationChange = (page: number, pageSize?: number) => {
    if (item) {
      getPosts({ pagination: { current: page, pageSize }, userId: item.id });
    }
  };

  const onFilterChange = () => {
    if (item) {
      getPosts({
        pagination,
        userId: item.id,
        filters: {
          ...currentFilterParams,
          sort:
            currentFilterParams.sort === 'createdDateTime,desc'
              ? 'createdDateTime,asc'
              : 'createdDateTime,desc',
        },
      });
    }
  };

  const onSearch = (searchValue: string) => {
    if (item) {
      getPosts({
        pagination,
        userId: item.id,
        filters: {
          ...currentFilterParams,
          text: searchValue,
        },
      });
    }
  };

  if (!preloaders.getPosts && !posts.length) {
    return <Empty />;
  }

  return (
    <>
      <div className={css.header}>
        <div className={css.headerSearch}>
          <Search
            allowClear
            onSearch={onSearch}
            placeholder={content.searchPlaceholder}
          />
        </div>
        <div className={css.headerActions}>
          <div className={css.dateSorter}>
            <Button
              icon={
                currentFilterParams.sort === 'createdDateTime,desc' ? (
                  <SortDescendingOutlined />
                ) : (
                  <SortAscendingOutlined />
                )
              }
              onClick={onFilterChange}
            >
              {content.dateSorter}
            </Button>
          </div>
          <Pagination
            {...pagination}
            onChange={paginationChange}
            showSizeChanger
            pageSizeOptions={pageSizeOptions}
          />
        </div>
      </div>
      <Spin spinning={!!preloaders.getPosts}>
        <div className={css.root}>
          {posts.map((post) => {
            const { id: postID } = post;
            const isLoadingHide =
              preloaders.hidePost === preloaderTexts.hidePost(postID) ||
              preloaders.unhidePost === preloaderTexts.unhidePost(postID);
            const isLoadingBlock = preloaders.block === preloaderTexts.blockPost(postID);
            const isLoadingFace =
              preloaders.facePost === preloaderTexts.facePost(postID) ||
              preloaders.removefacePost === preloaderTexts.removefacePost(postID);

            return (
              <PostPreview
                post={post}
                key={post.id}
                isLoadingHide={isLoadingHide}
                isLoadingBlock={isLoadingBlock}
                isLoadingFace={isLoadingFace}
                isDisabledBlock={isDisabledBlock}
                isDisabledHide={isDisabledHide}
                isDisabledFace={isDisabledFace}
                onBlock={handleBlockPost}
                onHide={handleHidePost}
                onFace={handleFacePost}
                onOpen={handleOpenPost}
              />
            );
          })}
        </div>
      </Spin>
    </>
  );
};

export default UserPosts;
